import React, { useState, useEffect, useRef } from 'react';
import { Grid, TextField, FormHelperText, Box } from '@mui/material';

import WorkerCountField from './WorkerCountField';
import WorkerCommandTypeField from './WorkerCommandTypeField';
import JOB_TYPES from '@/components/jobs/types';

export default function WorkerFields({
  initialValues,
  type,
  onChange,
  edit = false,
}) {
  const [worker_count, setWorkerCount] = useState(
    initialValues ? Math.max(initialValues.length, 1) : 1,
  );

  const [worker_command_type, setWorkerCommandType] = useState(
    type === JOB_TYPES.TRAINING.value ? 'different' : 'same',
  );

  const [worker_command, setWorkerCommand] = useState(
    type === JOB_TYPES.INFERENCE.value && initialValues && initialValues[0]
      ? initialValues[0].command
      : '',
  );

  const [workers, setWorkers] = useState(
    initialValues && initialValues.length
      ? initialValues
          .filter((worker) => worker != null)
          .map((worker) => {
            return {
              command: worker?.command || '',
            };
          })
      : [{ command: '' }],
  );

  const [worker_command_error, setWorkerCommandError] = useState(false);

  const [workers_errors, setWorkersErrors] = useState(
    initialValues ? new Array(initialValues.length).fill(false) : [],
  );

  const isInitialMount = useRef(true);

  // Initial mount - call onChange
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      const blank_commands = workers.filter((worker) => worker.command === '');
      onChange('workers', workers, Boolean(blank_commands.length));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update onChange when state changes
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }

    let workers_array;
    let command_blank;

    if (worker_command_type === 'same') {
      workers_array = new Array(worker_count).fill({
        command: worker_command,
      });
      command_blank = worker_command === '';
    } else {
      workers_array = workers;
      const blank_commands = workers.filter((worker) => worker.command === '');
      command_blank = Boolean(blank_commands.length);
    }

    const worker_errors_set = new Set(workers_errors);
    const has_error =
      worker_command_error || worker_errors_set.has(true) || command_blank;

    onChange('workers', workers_array, has_error);
  }, [
    worker_count,
    worker_command_type,
    worker_command,
    workers,
    worker_command_error,
    workers_errors,
    onChange,
  ]);

  const handleChange = (event, { name, value }) => {
    const array_re = /(?<key>.+)\[(?<number>[0-9]+)\]/;
    const result = name.match(array_re);

    if (result && result.groups.key === 'workers') {
      const index = parseInt(result.groups.number, 10);
      const updated_workers = [...workers];
      updated_workers[index] = {
        command: value,
      };

      const updated_workers_errors = [...workers_errors];
      updated_workers_errors[index] = value === '';

      setWorkers(updated_workers);
      setWorkersErrors(updated_workers_errors);
    } else {
      switch (name) {
        case 'worker_count':
          let updated_workers = [...workers];
          let updated_workers_errors = [...workers_errors];

          while (updated_workers.length < value) {
            updated_workers.push({ command: '' });
            updated_workers_errors.push(true);
          }
          while (updated_workers.length > value) {
            updated_workers.pop();
            updated_workers_errors.pop();
          }

          setWorkers(updated_workers);
          setWorkersErrors(updated_workers_errors);
          setWorkerCount(value);
          break;
        case 'worker_command':
          setWorkerCommand(value);
          setWorkerCommandError(value === '');
          break;
        case 'worker_command_type':
          setWorkerCommandType(value);
          break;
        default:
          break;
      }
    }
  };

  return (
    <Grid container spacing={2}>
      {type === JOB_TYPES.TRAINING.value ? (
        <>
          <Grid size={12}>
            <WorkerCountField
              value={worker_count}
              onChange={handleChange}
              disabled={edit}
            />
          </Grid>
          <Grid size={12}>
            <WorkerCommandTypeField
              value={worker_command_type}
              onChange={handleChange}
              disabled={edit}
            />
          </Grid>
        </>
      ) : undefined}
      {worker_command_type === 'same' ? (
        <Grid size={12}>
          <Box sx={{ width: '100%' }}>
            <TextField
              name='worker_command'
              label={
                type === JOB_TYPES.TRAINING.value
                  ? 'Command for Workers'
                  : 'Command for Worker'
              }
              value={worker_command || ''}
              required
              onChange={(event) => {
                handleChange(event, {
                  name: 'worker_command',
                  value: event.target.value.replace(/ \\ /g, ' '),
                });
              }}
              disabled={edit}
              error={worker_command_error}
              variant='outlined'
              fullWidth
              placeholder={
                type === JOB_TYPES.TRAINING.value
                  ? 'python train.py'
                  : 'python predict.py'
              }
            />
            {worker_command_error && (
              <FormHelperText error>Please enter a value</FormHelperText>
            )}
          </Box>
        </Grid>
      ) : (
        new Array(worker_count).fill().map((e, i) => {
          const worker = workers[i] || { command: '' };
          const error = workers_errors[i] || false;
          return (
            <Grid size={12} key={`workers[${i}]`}>
              <Box sx={{ width: '100%' }}>
                <TextField
                  name={`workers[${i}]`}
                  label={`Command for Worker ${i + 1}`}
                  value={worker.command || ''}
                  required
                  onChange={(event) => {
                    handleChange(event, {
                      name: `workers[${i}]`,
                      value: event.target.value.replace(/ \\ /g, ' '),
                    });
                  }}
                  disabled={edit}
                  error={error}
                  variant='outlined'
                  fullWidth
                  placeholder='python train.py'
                />
                {error && (
                  <FormHelperText error>Please enter a value</FormHelperText>
                )}
              </Box>
            </Grid>
          );
        })
      )}
    </Grid>
  );
}
